import {isDropboxEmoji} from 'components/DSYS/editor/plugins/EmojiPicker';
import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedEditor,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import {$applyNodeReplacement, createEditor, DecoratorNode} from 'lexical';
import {ReactNode, Suspense} from 'react';

import {ImageComponent} from './ImageComponent';
import styles from './ImageNode.module.css';

/**
 * `inline` is the original behaviour (image flows in its paragraph). `left`/`right` float the
 * image so the following text wraps beside it, giving a two-column image + text layout.
 */
export type ImageLayout = 'inline' | 'left' | 'right';

const LAYOUT_CLASS: Record<ImageLayout, string | undefined> = {
  inline: undefined,
  left: styles.wrapLeft,
  right: styles.wrapRight,
};

const normalizeLayout = (layout: unknown): ImageLayout =>
  layout === 'left' || layout === 'right' ? layout : 'inline';

const applyLayoutClass = (element: HTMLElement, layout: ImageLayout) => {
  Object.values(LAYOUT_CLASS).forEach((className) => {
    if (className) {
      element.classList.remove(className);
    }
  });
  const className = LAYOUT_CLASS[layout];
  if (className) {
    element.classList.add(className);
    // Lets the editor root contain the float (see `.content:has(...)` in Editor.module.css).
    element.setAttribute('data-image-layout', layout);
  } else {
    element.removeAttribute('data-image-layout');
  }
};

export interface ImagePayload {
  altText: string;
  caption?: LexicalEditor;
  height?: number;
  key?: NodeKey;
  showCaption?: boolean;
  src: string;
  width?: number;
  captionsEnabled?: boolean;
  layout?: ImageLayout;
}

function convertImageElement(domNode: Node): null | DOMConversionOutput {
  const img = domNode as HTMLImageElement;
  if (img.src.startsWith('file:///')) {
    return null;
  }
  const {alt: altText, src, width, height} = img;
  const layout = normalizeLayout(img.getAttribute('data-layout'));
  const node = $createImageNode({altText, height, src, width, layout});
  return {node};
}

export type SerializedImageNode = Spread<
  {
    altText: string;
    caption: SerializedEditor;
    height?: number;
    showCaption: boolean;
    src: string;
    width?: number;
    /** Omitted for `inline` so existing documents serialize unchanged. */
    layout?: Exclude<ImageLayout, 'inline'>;
  },
  SerializedLexicalNode
>;

export class ImageNode extends DecoratorNode<ReactNode> {
  __src: string;
  __altText: string;
  __width: 'inherit' | number;
  __height: 'inherit' | number;
  __showCaption: boolean;
  __caption: LexicalEditor;
  // Captions cannot yet be used within editor cells
  __captionsEnabled: boolean;
  __layout: ImageLayout;

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__width,
      node.__height,
      node.__showCaption,
      node.__caption,
      node.__captionsEnabled,
      node.__layout,
      node.__key
    );
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const {altText, height, width, caption, src, showCaption, layout} = serializedNode;
    const node = $createImageNode({
      altText,
      height,
      layout: normalizeLayout(layout),
      showCaption,
      src,
      width,
    });
    const nestedEditor = node.__caption;
    const editorState = nestedEditor.parseEditorState(caption.editorState);
    if (!editorState.isEmpty()) {
      nestedEditor.setEditorState(editorState);
    }
    return node;
  }

  exportDOM(): DOMExportOutput {
    // If this is a Dropbox emoji, export the emoji character as text instead of an image
    if (isDropboxEmoji(this.__src)) {
      const match = this.__src.match(/emoji\/([^.]+)\.png/);
      if (match) {
        const emojiId = match[1];
        const codePoints = emojiId.split('-').map((hex) => parseInt(hex, 16));
        const emojiChar = String.fromCodePoint(...codePoints);
        const textNode = document.createTextNode(emojiChar);
        return {element: textNode as any};
      }
    }

    // For regular images, export as img element
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__altText);
    element.setAttribute('width', this.__width.toString());
    element.setAttribute('height', this.__height.toString());
    if (this.__layout !== 'inline') {
      // Inline styles so the layout survives HTML consumers that don't load our CSS.
      element.setAttribute('data-layout', this.__layout);
      element.setAttribute(
        'style',
        `float: ${this.__layout}; width: 45%; height: auto; margin: ${
          this.__layout === 'left' ? '4px 24px 12px 0' : '4px 0 12px 24px'
        };`
      );
    }
    return {element};
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    };
  }

  constructor(
    src: string,
    altText: string,
    width?: 'inherit' | number,
    height?: 'inherit' | number,
    showCaption?: boolean,
    caption?: LexicalEditor,
    captionsEnabled?: boolean,
    layout?: ImageLayout,
    key?: NodeKey
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__width = width || 'inherit';
    this.__height = height || 'inherit';
    this.__showCaption = showCaption || false;
    this.__caption = caption || createEditor();
    this.__captionsEnabled = captionsEnabled || captionsEnabled === undefined;
    this.__layout = layout ?? 'inline';
  }

  exportJSON(): SerializedImageNode {
    return {
      altText: this.getAltText(),
      caption: this.__caption.toJSON(),
      height: this.__height === 'inherit' ? 0 : this.__height,
      showCaption: this.__showCaption,
      src: this.getSrc(),
      type: 'image',
      version: 1,
      width: this.__width === 'inherit' ? 0 : this.__width,
      ...(this.__layout !== 'inline' ? {layout: this.__layout} : {}),
    };
  }

  setWidthAndHeight(width: 'inherit' | number, height: 'inherit' | number): void {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  setSrc(src: string): void {
    const writable = this.getWritable();
    writable.__src = src;
  }

  getLayout(): ImageLayout {
    return this.getLatest().__layout;
  }

  setLayout(layout: ImageLayout): void {
    const writable = this.getWritable();
    writable.__layout = layout;
  }

  setShowCaption(showCaption: boolean): void {
    const writable = this.getWritable();
    writable.__showCaption = showCaption;
  }

  // View

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    const theme = config.theme;
    const className = theme.image;
    if (className !== undefined) {
      span.className = className;
    }
    applyLayoutClass(span, this.__layout);
    return span;
  }

  updateDOM(prevNode: ImageNode, dom: HTMLElement): false {
    if (prevNode.__layout !== this.__layout) {
      applyLayoutClass(dom, this.__layout);
    }
    return false;
  }

  getSrc(): string {
    return this.__src;
  }

  getAltText(): string {
    return this.__altText;
  }

  getTextContent(): string {
    // If this is an emoji image, return the actual emoji character for copy/paste as text
    if (isDropboxEmoji(this.__src)) {
      // Extract emoji ID from URL
      const match = this.__src.match(/emoji\/([^.]+)\.png/);
      if (match) {
        const emojiId = match[1];
        // Convert hex codepoint(s) to actual emoji character
        const codePoints = emojiId.split('-').map((hex) => parseInt(hex, 16));
        return String.fromCodePoint(...codePoints);
      }
    }
    // For non-emoji images, return empty string (images don't have text content)
    return '';
  }

  decorate() {
    return (
      <Suspense fallback={null}>
        <ImageComponent
          src={this.__src}
          altText={this.__altText}
          width={this.__width}
          height={this.__height}
          layout={this.__layout}
          nodeKey={this.getKey()}
        />
      </Suspense>
    );
  }
}

export function $createEmojiNode({
  altText,
  captionsEnabled,
  src,
  showCaption,
  caption,
  key,
}: ImagePayload): ImageNode {
  return $applyNodeReplacement(
    new ImageNode(src, altText, 18, 18, showCaption, caption, captionsEnabled, 'inline', key)
  );
}

export function $createImageNode({
  altText,
  height,
  layout,
  captionsEnabled,
  src,
  width,
  showCaption,
  caption,
  key,
}: ImagePayload): ImageNode {
  return $applyNodeReplacement(
    new ImageNode(src, altText, width, height, showCaption, caption, captionsEnabled, layout, key)
  );
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode;
}
